import React, { useContext, useEffect, useMemo, useState } from 'react';
import {
    Alert,
    AlertDescription,
    AlertIcon,
    AlertTitle,
    Badge,
    Box,
    Button,
    Code,
    Container,
    Divider,
    FormControl,
    FormHelperText,
    FormLabel,
    Grid,
    Heading,
    HStack,
    Input,
    List,
    ListIcon,
    ListItem,
    SimpleGrid,
    Spinner,
    Table,
    Tbody,
    Td,
    Text,
    Th,
    Thead,
    Tr,
    VStack,
    useToast,
} from '@chakra-ui/react';
import { CheckIcon, WarningIcon } from '@chakra-ui/icons';
import axios from 'axios';
import Navbar from '../compnents/Navbar';
import SideBar from '../compnents/SideBar';
import AuthContext from '../context/AuthContext';
import Loading from '../compnents/Loading';

const isProduction = process.env.NODE_ENV === 'production';
const API_BASE_URL = isProduction
    ? process.env.REACT_APP_API_BASE_URL_PROD
    : process.env.REACT_APP_API_BASE_URL_DEV;

const PUBLIC_API_BASE_URL = 'https://api.finansee.eu';

const normalizeApiKeys = (data) => {
    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.api_keys)) return data.api_keys;
    if (Array.isArray(data?.apiKeys)) return data.apiKeys;
    return [];
};

const formatDate = (value) => {
    if (!value) return 'Never';
    try {
        return new Intl.DateTimeFormat('en', {
            year: 'numeric',
            month: 'short',
            day: '2-digit',
        }).format(new Date(value));
    } catch {
        return value;
    }
};

function DeveloperPortal() {
    const { token, user, isAuthInitializing } = useContext(AuthContext);
    const toast = useToast();
    const [loading, setLoading] = useState(true);
    const [apiKeys, setApiKeys] = useState([]);
    const [createdPlainKey, setCreatedPlainKey] = useState('');
    const [formData, setFormData] = useState({
        name: 'Sandbox integration key',
        description: 'Created from FinanSee Playground developer portal',
        scopes: 'read:customers,read:scores,read:benchmarks',
    });
    const [creating, setCreating] = useState(false);
    const [revokingId, setRevokingId] = useState('');
    const [errorMessage, setErrorMessage] = useState('');

    const headers = useMemo(() => ({
        Authorization: `Bearer ${token}`,
    }), [token]);

    const fetchApiKeys = async () => {
        if (!token) return;

        try {
            setLoading(true);
            setErrorMessage('');
            const { data } = await axios.get(`${API_BASE_URL}/content/api-keys`, {
                headers,
                params: { include_inactive: true },
            });
            setApiKeys(normalizeApiKeys(data));
        } catch (error) {
            const message = error.response?.data?.message || 'Unable to load API keys';
            setErrorMessage(message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchApiKeys();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token]);

    const handleCreateApiKey = async () => {
        const name = formData.name.trim();
        const scopes = formData.scopes
            .split(',')
            .map((scope) => scope.trim())
            .filter(Boolean);

        if (!name) {
            toast({
                title: 'API key name required',
                status: 'warning',
                duration: 2500,
                isClosable: true,
            });
            return;
        }

        try {
            setCreating(true);
            setCreatedPlainKey('');
            const { data } = await axios.post(
                `${API_BASE_URL}/content/api-keys`,
                {
                    name,
                    description: formData.description.trim(),
                    scopes,
                },
                { headers }
            );

            setCreatedPlainKey(data.plain_key || data.api_key_secret || data.key || '');
            await fetchApiKeys();
            toast({
                title: 'API key created',
                description: 'Copy the secret now. It will not be shown again.',
                status: 'success',
                duration: 4000,
                isClosable: true,
            });
        } catch (error) {
            toast({
                title: 'Unable to create API key',
                description: error.response?.data?.message || 'Check the API service configuration and try again.',
                status: 'error',
                duration: 5000,
                isClosable: true,
            });
        } finally {
            setCreating(false);
        }
    };

    const handleRevokeApiKey = async (id) => {
        try {
            setRevokingId(id);
            await axios.post(
                `${API_BASE_URL}/content/api-keys/${id}/revoke`,
                { reason: 'Revoked from Playground developer portal' },
                { headers }
            );
            await fetchApiKeys();
            toast({
                title: 'API key revoked',
                status: 'success',
                duration: 2500,
                isClosable: true,
            });
        } catch (error) {
            toast({
                title: 'Unable to revoke API key',
                description: error.response?.data?.message || 'Please retry from the developer portal.',
                status: 'error',
                duration: 5000,
                isClosable: true,
            });
        } finally {
            setRevokingId('');
        }
    };

    if (isAuthInitializing || !token) {
        return <Loading />;
    }

    if (!user) {
        return (
            <Box minH="100vh" display="flex" alignItems="center" justifyContent="center">
                <Spinner size="xl" color="brand.teal" />
            </Box>
        );
    }

    return (
        <Box minH="100vh" display="flex" flexDirection="column" bg="brand.bg">
            <Navbar />
            <Box display="flex" flex="1">
                <SideBar />
                <Container maxW="container.xl" flex="1" py={8}>
                    <VStack align="stretch" spacing={8}>
                        <Box
                            bg="white"
                            borderRadius="2xl"
                            borderWidth="1px"
                            borderColor="brand.divider"
                            boxShadow="0 24px 80px rgba(15, 23, 42, 0.08)"
                            p={{ base: 5, md: 8 }}
                        >
                            <HStack spacing={3} mb={3} flexWrap="wrap">
                                <Badge colorScheme="purple" borderRadius="full" px={3} py={1}>
                                    Business gated
                                </Badge>
                                <Badge colorScheme="blue" borderRadius="full" px={3} py={1}>
                                    Admin only
                                </Badge>
                            </HStack>
                            <Heading size="xl" color="gray.900" mb={3}>
                                Developer Portal
                            </Heading>
                            <Text color="gray.600" fontSize="lg" maxW="4xl">
                                Manage tenant-scoped API keys and validate integrations against the canonical FinanSee API contract.
                                This sandbox is not part of Solo or Team. Production integrations are scoped as Enterprise projects.
                            </Text>
                        </Box>

                        <SimpleGrid columns={{ base: 1, md: 3 }} spacing={5}>
                            <Box bg="white" borderRadius="xl" p={5} borderWidth="1px" borderColor="gray.200">
                                <Heading size="sm" mb={2}>API contract</Heading>
                                <Text color="gray.600" fontSize="sm" mb={4}>
                                    Docs are served inside this gated workspace. Public Swagger and OpenAPI routes are disabled in production.
                                </Text>
                                <Code p={2} borderRadius="md" display="block">{PUBLIC_API_BASE_URL}</Code>
                            </Box>
                            <Box bg="white" borderRadius="xl" p={5} borderWidth="1px" borderColor="gray.200">
                                <Heading size="sm" mb={2}>OpenAPI schema</Heading>
                                <Text color="gray.600" fontSize="sm" mb={4}>
                                    Schema exports are shared during Business or enterprise scoping, after API access is approved.
                                </Text>
                                <Badge colorScheme="purple">Gated contract artefact</Badge>
                            </Box>
                            <Box bg="white" borderRadius="xl" p={5} borderWidth="1px" borderColor="gray.200">
                                <Heading size="sm" mb={2}>Authentication</Heading>
                                <Text color="gray.600" fontSize="sm" mb={4}>
                                    Machine requests use the tenant key in the request header.
                                </Text>
                                <Code p={2} borderRadius="md" display="block">X-API-Key: fs_live_...</Code>
                            </Box>
                        </SimpleGrid>

                        <Box bg="white" borderRadius="2xl" p={6} borderWidth="1px" borderColor="gray.200">
                            <Heading size="md" mb={2}>Core endpoint reference</Heading>
                            <Text color="gray.600" fontSize="sm" mb={4}>
                                Use this reference for sandbox planning. Production endpoint scope is confirmed during Business or enterprise onboarding.
                            </Text>
                            <Box overflowX="auto">
                                <Table size="sm">
                                    <Thead>
                                        <Tr>
                                            <Th>Area</Th>
                                            <Th>Endpoint</Th>
                                            <Th>Auth</Th>
                                            <Th>Purpose</Th>
                                        </Tr>
                                    </Thead>
                                    <Tbody>
                                        {[
                                            ['Authentication', '/v1/auth/login', 'Bearer JWT', 'User login and admin session creation.'],
                                            ['Customer ingest', '/v1/ingest/customers', 'Bearer JWT', 'Upload approved CSV/XLSX customer datasets.'],
                                            ['Batch scoring', '/v1/score/batch', 'Bearer JWT or scoped API key', 'Run behavioral scoring for tenant customers.'],
                                            ['Benchmarks', '/v1/benchmark', 'Bearer JWT or scoped API key', 'Compare tenant signals against approved benchmarks.'],
                                            ['API key verify', '/v1/api-keys/verify', 'X-API-Key', 'Validate a tenant machine credential.'],
                                        ].map(([area, endpoint, auth, purpose]) => (
                                            <Tr key={endpoint}>
                                                <Td fontWeight="semibold">{area}</Td>
                                                <Td><Code>{endpoint}</Code></Td>
                                                <Td>{auth}</Td>
                                                <Td color="gray.600">{purpose}</Td>
                                            </Tr>
                                        ))}
                                    </Tbody>
                                </Table>
                            </Box>
                        </Box>

                        <Grid templateColumns={{ base: '1fr', lg: '0.95fr 1.4fr' }} gap={6}>
                            <Box bg="white" borderRadius="2xl" p={6} borderWidth="1px" borderColor="gray.200">
                                <Heading size="md" mb={4}>
                                    Create API key
                                </Heading>
                                <VStack align="stretch" spacing={4}>
                                    <FormControl isRequired>
                                        <FormLabel>Name</FormLabel>
                                        <Input
                                            value={formData.name}
                                            onChange={(event) => setFormData({ ...formData, name: event.target.value })}
                                        />
                                    </FormControl>
                                    <FormControl>
                                        <FormLabel>Description</FormLabel>
                                        <Input
                                            value={formData.description}
                                            onChange={(event) => setFormData({ ...formData, description: event.target.value })}
                                        />
                                    </FormControl>
                                    <FormControl>
                                        <FormLabel>Scopes</FormLabel>
                                        <Input
                                            value={formData.scopes}
                                            onChange={(event) => setFormData({ ...formData, scopes: event.target.value })}
                                        />
                                        <FormHelperText>Comma-separated, for example read:customers, read:scores.</FormHelperText>
                                    </FormControl>
                                    <Button
                                        colorScheme="blue"
                                        onClick={handleCreateApiKey}
                                        isLoading={creating}
                                        loadingText="Creating"
                                    >
                                        Create tenant API key
                                    </Button>
                                </VStack>

                                {createdPlainKey && (
                                    <Alert status="warning" borderRadius="lg" mt={5}>
                                        <AlertIcon />
                                        <Box>
                                            <AlertTitle>Copy this secret now</AlertTitle>
                                            <AlertDescription>
                                                <Code whiteSpace="normal" wordBreak="break-all" mt={2} p={2} display="block">
                                                    {createdPlainKey}
                                                </Code>
                                            </AlertDescription>
                                        </Box>
                                    </Alert>
                                )}
                            </Box>

                            <Box bg="white" borderRadius="2xl" p={6} borderWidth="1px" borderColor="gray.200">
                                <HStack justify="space-between" align="start" mb={4}>
                                    <Box>
                                        <Heading size="md">Tenant API keys</Heading>
                                        <Text fontSize="sm" color="gray.600" mt={1}>
                                            Keys are scoped to this tenant and should be rotated when access changes.
                                        </Text>
                                    </Box>
                                    <Button size="sm" variant="outline" onClick={fetchApiKeys} isLoading={loading}>
                                        Refresh
                                    </Button>
                                </HStack>

                                {errorMessage && (
                                    <Alert status="error" borderRadius="lg" mb={4}>
                                        <AlertIcon />
                                        <AlertDescription>{errorMessage}</AlertDescription>
                                    </Alert>
                                )}

                                {loading ? (
                                    <Box py={10} textAlign="center">
                                        <Spinner color="brand.teal" />
                                    </Box>
                                ) : apiKeys.length === 0 ? (
                                    <Alert status="info" borderRadius="lg">
                                        <AlertIcon />
                                        <AlertDescription>No API keys have been created for this tenant yet.</AlertDescription>
                                    </Alert>
                                ) : (
                                    <Box overflowX="auto">
                                        <Table size="sm">
                                            <Thead>
                                                <Tr>
                                                    <Th>Name</Th>
                                                    <Th>Prefix</Th>
                                                    <Th>Scopes</Th>
                                                    <Th>Status</Th>
                                                    <Th>Created</Th>
                                                    <Th textAlign="right">Action</Th>
                                                </Tr>
                                            </Thead>
                                            <Tbody>
                                                {apiKeys.map((key) => (
                                                    <Tr key={key.id}>
                                                        <Td>
                                                            <Text fontWeight="semibold">{key.name}</Text>
                                                            {key.description && (
                                                                <Text fontSize="xs" color="gray.500">{key.description}</Text>
                                                            )}
                                                        </Td>
                                                        <Td><Code>{key.key_prefix || key.prefix || 'hidden'}</Code></Td>
                                                        <Td>
                                                            {(key.scopes || []).map((scope) => (
                                                                <Badge key={scope} mr={1} mb={1}>{scope}</Badge>
                                                            ))}
                                                        </Td>
                                                        <Td>
                                                            <Badge colorScheme={key.is_active === false ? 'red' : 'green'}>
                                                                {key.is_active === false ? 'Revoked' : 'Active'}
                                                            </Badge>
                                                        </Td>
                                                        <Td>{formatDate(key.created_at)}</Td>
                                                        <Td textAlign="right">
                                                            <Button
                                                                size="xs"
                                                                colorScheme="red"
                                                                variant="outline"
                                                                isDisabled={key.is_active === false}
                                                                isLoading={revokingId === key.id}
                                                                onClick={() => handleRevokeApiKey(key.id)}
                                                            >
                                                                Revoke
                                                            </Button>
                                                        </Td>
                                                    </Tr>
                                                ))}
                                            </Tbody>
                                        </Table>
                                    </Box>
                                )}
                            </Box>
                        </Grid>

                        <Box bg="brand.tealSoft" borderRadius="2xl" p={6} borderWidth="1px" borderColor="brand.divider">
                            <Heading size="md" mb={4}>Release checklist</Heading>
                            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                                {[
                                    'Use synthetic data in sandbox keys before production data.',
                                    'Store API secrets outside source control and shared documents.',
                                    'Rotate keys before changing integration owners.',
                                    'Use OpenAPI as the source of truth for endpoint contracts.',
                                ].map((item) => (
                                    <List key={item} spacing={2}>
                                        <ListItem display="flex" alignItems="flex-start">
                                            <ListIcon as={CheckIcon} color="green.500" mt={1} />
                                            <Text color="gray.700">{item}</Text>
                                        </ListItem>
                                    </List>
                                ))}
                            </SimpleGrid>
                            <Divider my={5} />
                            <HStack align="start" color="orange.700">
                                <WarningIcon mt={1} />
                                <Text fontSize="sm">
                                    Production API enablement still requires commercial approval, DPA alignment, and endpoint scope confirmation.
                                </Text>
                            </HStack>
                        </Box>
                    </VStack>
                </Container>
            </Box>
        </Box>
    );
}

export default DeveloperPortal;
